/**
 * The app-wide pop-ups for workflow runs that just settled.
 *
 * Sibling of the upload tray: mounted once above the router, so a run started
 * on /templates can announce itself on /chat. They float top-right rather than
 * bottom-right so they never land on top of the tray when both are on screen.
 *
 * This is a STACK, not a slot. Two extractions queued together settle seconds
 * apart, and a single slot would silently overwrite the first outcome with the
 * second — the one case where the user is guaranteed to be waiting on both.
 *
 * The inline `WorkflowToast` strip stays what it is — a page message for things
 * a page itself did (a workflow saved, a run refused). These are only ever
 * about a run reaching its end.
 */

import { X } from "lucide-react";
import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
	useWorkflowRunNotifier,
	type WorkflowRunNotification,
} from "../../context/WorkflowRunNotifier";
import type { ToastVariant } from "./WorkflowToast";

const TOAST_STYLES: Record<ToastVariant, string> = {
	success: "border-emerald-200 bg-emerald-50 text-emerald-800",
	warning: "border-amber-200 bg-amber-50 text-amber-800",
	error: "border-red-200 bg-red-50 text-red-800",
	info: "border-gray-200 bg-white text-text-base",
};

/** A KPI workflow's own page — where a run's history and outcome live. */
const kpiDashboardPath = (workflowId: string): string =>
	`/workflows/kpi/${encodeURIComponent(workflowId)}?tab=history`;

/** Any `/workflows/kpi/:id`. Matching the prefix is deliberate: the outcome is
 *  on screen whichever of that workflow's tabs is open. */
const KPI_DASHBOARD_PATTERN = /^\/workflows\/kpi\/[^/]+$/;

interface WorkflowRunToastListProps {
	notifications: WorkflowRunNotification[];
	/** Hidden when the user is already looking at the run history. */
	showRunHistoryLink: boolean;
	onViewRunHistory: (notification: WorkflowRunNotification) => void;
	onDismiss: (id: number) => void;
}

export const WorkflowRunToastList: React.FC<WorkflowRunToastListProps> = ({
	notifications,
	showRunHistoryLink,
	onViewRunHistory,
	onDismiss,
}) => {
	if (notifications.length === 0) return null;
	// Top-center: the general toast stack owns `fixed top-4 right-4 z-[60]`
	// (ToastContext) and UploadTray owns the bottom-right corner, so workflow
	// completions get their own region instead of painting over either.
	return (
		<div className="fixed top-4 left-1/2 z-50 flex w-80 max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-col gap-2">
			{notifications.map((notification) => (
				<div
					key={notification.id}
					role="status"
					className={`rounded-lg border px-3 py-2 text-sm shadow-lg ${TOAST_STYLES[notification.variant]}`}
				>
					<div className="flex items-start gap-2">
						<span className="flex-1">{notification.text}</span>
						<button
							type="button"
							aria-label="Dismiss notification"
							onClick={() => onDismiss(notification.id)}
							className="opacity-70 hover:opacity-100"
						>
							<X className="w-4 h-4" />
						</button>
					</div>
					{showRunHistoryLink && (
						<button
							type="button"
							onClick={() => onViewRunHistory(notification)}
							className="mt-1.5 text-sm font-medium underline underline-offset-2 opacity-80 hover:opacity-100"
						>
							View run history
						</button>
					)}
				</div>
			))}
		</div>
	);
};

const WorkflowRunToast: React.FC = () => {
	const { notifications, dismissNotification } = useWorkflowRunNotifier();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	// Suppression keys on the workflow PAGE, not on a list of them (DOP-478).
	// The index used to be `/templates`, where every workflow's card was on
	// screen; now the run's outcome redraws on its own workflow, and that is
	// the only place a floating copy of it is redundant.
	const onDashboardPage = KPI_DASHBOARD_PATTERN.test(pathname);

	/**
	 * On the workflow the outcome is already on screen — the header and the
	 * History tab redraw themselves the moment a run settles — so a floating
	 * toast saying the same thing is noise on top of the answer.
	 *
	 * Suppressed AND retired, in the render layer: the announcement is still
	 * made (the desktop notification, which only fires on a hidden tab, is
	 * untouched), it is simply consumed here. Leaving it in the queue would
	 * make it pop up minutes later on whatever page the user opened next,
	 * which is a worse version of the same noise.
	 */
	useEffect(() => {
		if (!onDashboardPage) return;
		for (const notification of notifications) {
			dismissNotification(notification.id);
		}
	}, [onDashboardPage, notifications, dismissNotification]);

	if (onDashboardPage) return null;

	return (
		<WorkflowRunToastList
			notifications={notifications}
			showRunHistoryLink={!onDashboardPage}
			onViewRunHistory={(notification) => {
				dismissNotification(notification.id);
				navigate(kpiDashboardPath(notification.workflowId));
			}}
			onDismiss={dismissNotification}
		/>
	);
};

export default WorkflowRunToast;
